import { hasValue } from '@repo/utils';
import { action, computed, makeObservable, observable } from 'mobx';
import { v4 } from 'uuid';

import type { TTrajectory } from '../../types/trajectory';
import { sortTrajectoryPointsByMd } from '../../utils/sort-trajectory-points-by-md';
import type { TFactTrajectoryPoint } from '../model';

import { areComparableTrajectorySnapshotsEqual, toComparableTrajectorySnapshot } from './fact-trajectory-comparable';
import { FactTrajectoryPointEntity } from './fact-trajectory-point.entity';

export type TFactTrajectoryData = Omit<TTrajectory, 'trajectoryStations' | 'parentTrajectoryStations'>;

export type TrajectoryPointMdValidationError = 'atOrBelowTieOn' | 'atOrBelowPrevious';

export class FactTrajectoryEntity {
  readonly entityId = v4();

  @observable.ref private _initialData: TFactTrajectoryData;

  @observable.ref data: TFactTrajectoryData;

  @observable.ref private _points: FactTrajectoryPointEntity[] = [];
  @observable.ref private _initialPointIds: string[] = [];
  readonly pointsMap = observable.map<string, FactTrajectoryPointEntity>();

  constructor(initialData: TFactTrajectoryData, initialPoints: FactTrajectoryPointEntity[]) {
    this._initialData = structuredClone(initialData);
    this.data = structuredClone(initialData);

    this._points = sortTrajectoryPointsByMd(initialPoints);
    this._initialPointIds = this._points.map((point) => point.entityId);
    this.pointsMap.replace(this._points.map((point) => [point.entityId, point]));

    makeObservable(this);
  }

  @computed
  get id(): number {
    return this.data.id;
  }

  @computed
  get wellboreId(): number {
    return this.data.wellboreId;
  }

  @computed
  get title(): string {
    return this.data.citation?.title || String(this.data.id);
  }

  @computed
  get points(): FactTrajectoryPointEntity[] {
    return this._points;
  }

  get initialData(): TFactTrajectoryData {
    return this._initialData;
  }

  @computed
  get mdTieOnMeasuredDepth(): number | null {
    const value = this.data.mdTieOn?.measuredDepth?.value;

    if (typeof value !== 'number' || Number.isNaN(value)) {
      return null;
    }

    return value;
  }

  /** GoStream-sourced trajectories: all stations are read-only. */
  @computed
  get isStationsSourceLocked(): boolean {
    return this.data.source === 'gostream';
  }

  isMdAtOrBelowTieOn(md: number | null | undefined): boolean {
    const tieOn = this.mdTieOnMeasuredDepth;

    if (tieOn === null || typeof md !== 'number' || Number.isNaN(md)) {
      return false;
    }

    return md <= tieOn;
  }

  validatePointMd(entityId: string, md: number | null | undefined): TrajectoryPointMdValidationError | null {
    if (typeof md !== 'number' || Number.isNaN(md)) {
      return null;
    }

    if (this.isMdAtOrBelowTieOn(md)) {
      return 'atOrBelowTieOn';
    }

    const pointIndex = this._points.findIndex((point) => point.entityId === entityId);

    if (pointIndex <= 0) {
      return null;
    }

    const previousMd = this._points[pointIndex - 1]?.data.md;

    if (typeof previousMd !== 'number' || Number.isNaN(previousMd)) {
      return null;
    }

    if (md <= previousMd) {
      return 'atOrBelowPrevious';
    }

    return null;
  }

  isPointLockedByMdTieOn(point: FactTrajectoryPointEntity | null | undefined): boolean {
    if (!point) {
      return false;
    }

    return this.isMdAtOrBelowTieOn(point.data.md);
  }

  isPointLocked(point: FactTrajectoryPointEntity | null | undefined): boolean {
    if (point?.isParentStation || this.isStationsSourceLocked) {
      return true;
    }

    return this.isPointLockedByMdTieOn(point);
  }

  @computed
  get hasPointChanges(): boolean {
    if (this._points.length !== this._initialPointIds.length) {
      return true;
    }

    for (let i = 0; i < this._points.length; i++) {
      if (this._points[i]!.entityId !== this._initialPointIds[i]) {
        return true;
      }
    }

    return this._points.some((point) => point.hasChanges);
  }

  @computed
  get hasChanges(): boolean {
    if (this.data.id < 0) {
      return true;
    }

    return !areComparableTrajectorySnapshotsEqual(
      toComparableTrajectorySnapshot(this.data),
      toComparableTrajectorySnapshot(this._initialData),
    );
  }

  @action.bound
  updateData(
    patch: Partial<Omit<TFactTrajectoryData, 'citation'>> & { citation?: Partial<TTrajectory['citation']> },
  ): void {
    const { citation, ...rest } = patch;
    const next: TFactTrajectoryData = {
      ...this.data,
      ...rest,
    };

    if (citation) {
      next.citation = {
        ...this.data.citation,
        ...citation,
      };
    }

    this.data = next;
  }

  @action.bound
  resetData(): void {
    this.data = structuredClone(this._initialData);
  }

  @action.bound
  commitData(): void {
    this._initialData = structuredClone(this.data);
    this.commitPoints();
  }

  @action.bound
  commitPoints(): void {
    this._initialPointIds = this._points.map((point) => point.entityId);

    for (const point of this._points) {
      point.commitData();
    }
  }

  @action.bound
  syncPointsFromPatches(patches: Partial<TFactTrajectoryPoint>[]): void {
    const parentPoints = this._points.filter((point) => point.isParentStation);
    const ownPoints = this._points.filter((point) => !point.isParentStation);
    const next: FactTrajectoryPointEntity[] = [];

    for (let i = 0; i < patches.length; i++) {
      const patch = patches[i] ?? {};
      const existing = ownPoints[i];

      if (existing) {
        existing.updateData(patch);
        next.push(existing);
        continue;
      }

      next.push(new FactTrajectoryPointEntity(patch));
    }

    const keptIds = new Set([...next, ...parentPoints].map((point) => point.entityId));

    for (const point of this._points) {
      if (!keptIds.has(point.entityId)) {
        this.pointsMap.delete(point.entityId);
      }
    }

    this._points = [...next, ...parentPoints];
    this.pointsMap.replace(this._points.map((point) => [point.entityId, point]));
    this.sortPointsByMdAsc();
  }

  @action.bound
  replaceParentPoints(points: FactTrajectoryPointEntity[]): void {
    for (const point of this._points) {
      if (point.isParentStation) {
        this.pointsMap.delete(point.entityId);
      }
    }

    const ownPoints = this._points.filter((point) => !point.isParentStation);

    for (const point of points) {
      this.pointsMap.set(point.entityId, point);
    }

    this._points = [...ownPoints, ...points];
    this.sortPointsByMdAsc();
  }

  @action.bound
  private sortPointsByMdAsc(): void {
    if (this._points.length < 2) {
      return;
    }

    this._points = sortTrajectoryPointsByMd(this._points);
  }

  getPointField<K extends keyof TFactTrajectoryPoint>(entityId: string, field: K): TFactTrajectoryPoint[K] | null {
    const point = this.pointsMap.get(entityId);

    if (!point) {
      return null;
    }

    return point.data[field] ?? null;
  }

  @action.bound
  updatePoint(entityId: string, patch: Partial<TFactTrajectoryPoint>): void {
    this.pointsMap.get(entityId)?.updateData(patch);
  }

  @action.bound
  addPoints(points: FactTrajectoryPointEntity[]): void {
    if (points.length === 0) {
      return;
    }

    this._points = [...this._points, ...points];

    for (const point of points) {
      this.pointsMap.set(point.entityId, point);
    }
  }

  @action.bound
  addPoint(point: FactTrajectoryPointEntity): void {
    this.addPoints([point]);
  }

  @action.bound
  removePoint(point: FactTrajectoryPointEntity): void {
    this._points = this._points.filter((item) => item.entityId !== point.entityId);
    this.pointsMap.delete(point.entityId);
  }

  /** Removes selected stations and all points with MD >= the smallest selected MD.
   * Locked stations (mdTieOn / gostream source) are never removed. */
  @action.bound
  removePointsFromUppermost(entityIds: string[]): string[] {
    if (entityIds.length === 0 || this._points.length === 0) {
      return [];
    }

    const selectedIds = new Set(
      entityIds.filter((id) => {
        const point = this.pointsMap.get(id);

        return point ? !this.isPointLocked(point) : false;
      }),
    );

    if (selectedIds.size === 0) {
      return [];
    }

    let minMd: number | null = null;

    for (const point of this._points) {
      if (!selectedIds.has(point.entityId)) {
        continue;
      }

      const md = point.data.md;

      if (typeof md !== 'number') {
        continue;
      }

      if (minMd === null || md < minMd) {
        minMd = md;
      }
    }

    const idsToRemove = new Set<string>();

    if (minMd !== null) {
      for (const id of selectedIds) {
        if (this.pointsMap.has(id)) {
          idsToRemove.add(id);
        }
      }

      for (const point of this._points) {
        if (this.isPointLocked(point)) {
          continue;
        }

        const md = point.data.md;

        if (typeof md === 'number' && md >= minMd) {
          idsToRemove.add(point.entityId);
        }
      }
    } else {
      const firstIndex = this._points.findIndex((point) => selectedIds.has(point.entityId));

      if (firstIndex < 0) {
        return [];
      }

      for (let i = firstIndex; i < this._points.length; i++) {
        const point = this._points[i]!;

        if (!this.isPointLocked(point)) {
          idsToRemove.add(point.entityId);
        }
      }
    }

    const removedIds = [...idsToRemove];
    this._points = this._points.filter((point) => !idsToRemove.has(point.entityId));

    for (const id of removedIds) {
      this.pointsMap.delete(id);
    }

    return removedIds;
  }
}

export function isLocalNewTrajectoryId(id: number | null | undefined): boolean {
  return hasValue(id) && id < 0;
}
